import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ArrowRight, Check, Send,
  MapPin, DollarSign, Mail, MessageCircle,
  History, ChevronDown, ChevronUp, Package
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AiModelSelect, useAiModel } from "../../components/admin/AiModelSelect";

const TAG_LABELS = {
  best_value: { label: "Best Value", color: "bg-green-500" },
  price_drop: { label: "Price Drop", color: "bg-orange-500" },
  hidden_deal: { label: "Hidden Deal", color: "bg-[#0A2342]" },
  move_in_special: { label: "Move-In Special", color: "bg-amber-500" },
  luxury_find: { label: "Luxury Find", color: "bg-purple-500" },
  no_deposit: { label: "No Deposit", color: "bg-emerald-500" },
  pet_friendly: { label: "Pet Friendly", color: "bg-blue-500" },
  top_rated: { label: "Top Rated", color: "bg-red-500" },
};

const TopPicks = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [aiModel, setAiModel] = useAiModel();

  // Step state
  const [step, setStep] = useState("select_lead"); // select_lead, match_deals, review, history
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [matchedDeals, setMatchedDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState({}); // {dealId: {deal, note, tag}}
  const [sessions, setSessions] = useState([]);
  const [sending, setSending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [leadsRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/leads`, { headers }),
          axios.get(`${API}/top-picks`, { headers })
        ]);
        setLeads(leadsRes.data);
        setSessions(sessionsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchInitial();
  }, [headers]);

  const selectLead = async (lead) => {
    setSelectedLead(lead);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/deal-inventory/match/${lead.id}`, { headers });
      setMatchedDeals(res.data.matches);
      setSelectedDeals({});
      setStep("match_deals");
    } catch {
      toast({ title: "Failed to match deals", variant: "destructive" });
    }
    setLoading(false);
  };

  const toggleDeal = (deal) => {
    setSelectedDeals(prev => {
      const copy = { ...prev };
      if (copy[deal.id]) {
        delete copy[deal.id];
      } else {
        copy[deal.id] = {
          deal_id: deal.id,
          property_name: deal.property_name,
          base_rent: deal.base_rent,
          total_monthly_cost: deal.total_monthly_cost,
          area: deal.area,
          bedrooms: deal.bedrooms,
          marketing_tag: deal.marketing_tag || "",
          marketing_note: deal.marketing_note || ""
        };
      }
      return copy;
    });
  };

  const updatePickDetail = (dealId, field, value) => {
    setSelectedDeals(prev => ({
      ...prev,
      [dealId]: { ...prev[dealId], [field]: value }
    }));
  };

  const createSession = async () => {
    const picks = Object.values(selectedDeals);
    if (picks.length === 0) return toast({ title: "Select at least one deal", variant: "destructive" });
    if (picks.length > 5) return toast({ title: "Maximum 5 picks per session", variant: "destructive" });
    setLoading(true);
    try {
      const res = await axios.post(`${API}/top-picks`, {
        lead_id: selectedLead.id,
        deals: picks
      }, { headers });
      toast({ title: `Top ${picks.length} Picks saved!` });
      setSessions(prev => [res.data, ...prev]);
      setStep("review");
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    }
    setLoading(false);
  };

  const sendTopPicks = async (sessionId, sendVia) => {
    setSending(sessionId + sendVia);
    try {
      const res = await axios.post(`${API}/top-picks/send`, { session_id: sessionId, send_via: sendVia, model: aiModel }, { headers });
      const methods = [];
      if (res.data.email_sent) methods.push("Email");
      if (res.data.sms_sent) methods.push("SMS");
      toast({ title: `Top Picks sent via ${methods.join(" + ")}!` });
      // Refresh sessions
      const sessionsRes = await axios.get(`${API}/top-picks`, { headers });
      setSessions(sessionsRes.data);
    } catch (err) {
      toast({ title: "Send failed", description: err.response?.data?.detail || "Error", variant: "destructive" });
    }
    setSending(null);
  };

  const filteredLeads = leads.filter(l =>
    !searchQ || l.name?.toLowerCase().includes(searchQ.toLowerCase()) || l.email?.toLowerCase().includes(searchQ.toLowerCase())
  );

  const picksCount = Object.keys(selectedDeals).length;

  return (
    <div className="space-y-6" data-testid="top-picks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="top-picks-title">
            {step === "select_lead" ? "Top Picks Generator" : step === "match_deals" ? "Match & Select Deals" : step === "review" ? "Review & Send" : "Top Picks History"}
          </h1>
          <p className="text-slate-600">
            {step === "select_lead" ? "Select a lead to find matching deals" :
             step === "match_deals" ? `Matching deals for ${selectedLead?.name} — pick your top 3-5` :
             step === "review" ? "Review saved sessions and send to leads" : "Past sessions"}
          </p>
        </div>
        <div className="flex gap-2">
          {step !== "select_lead" && (
            <button onClick={() => { setStep("select_lead"); setSelectedLead(null); setSelectedDeals({}); }}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50" data-testid="back-to-leads">
              &larr; Pick Another Lead
            </button>
          )}
          <button onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1" data-testid="toggle-history">
            <History className="w-4 h-4" /> History ({sessions.length})
          </button>
        </div>
      </div>

      {/* Step 1: Select Lead */}
      {step === "select_lead" && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search leads by name or email..." value={searchQ} onChange={e => setSearchQ(e.target.value)}
              className="input-field pl-10 text-sm" data-testid="lead-search" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-[60vh] overflow-y-auto" data-testid="leads-select-list">
            {loading ? (
              <div className="p-12 text-center"><span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2342] inline-block" /></div>
            ) : filteredLeads.length === 0 ? (
              <div className="p-12 text-center text-slate-500">No leads found</div>
            ) : filteredLeads.map(lead => (
              <button key={lead.id} onClick={() => selectLead(lead)}
                className="w-full p-4 px-6 flex items-center justify-between hover:bg-slate-50 transition-colors text-left" data-testid={`select-lead-${lead.id}`}>
                <div>
                  <p className="font-medium text-[#0A2342]">{lead.name}</p>
                  <p className="text-sm text-slate-500">{lead.email} {lead.phone ? `| ${lead.phone}` : ""}</p>
                  <div className="flex gap-2 mt-1 text-xs text-slate-400">
                    {lead.budget_max && <span><DollarSign className="w-3 h-3 inline" />${lead.budget_min?.toLocaleString()}-${lead.budget_max?.toLocaleString()}</span>}
                    {lead.bedrooms && <span>{lead.bedrooms}</span>}
                    {lead.preferred_areas?.length > 0 && <span><MapPin className="w-3 h-3 inline" />{lead.preferred_areas.join(", ")}</span>}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Match & Select Deals */}
      {step === "match_deals" && (
        <div className="space-y-4">
          {/* Lead summary */}
          <div className="bg-[#0A2342] rounded-xl p-5 text-white">
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <p className="font-bold text-lg">{selectedLead?.name}</p>
                <p className="text-sm text-slate-300">{selectedLead?.email}</p>
              </div>
              <div className="flex gap-4 text-sm">
                {selectedLead?.budget_max && <span className="bg-white/10 px-3 py-1 rounded-lg">${selectedLead.budget_min?.toLocaleString()}-${selectedLead.budget_max?.toLocaleString()}/mo</span>}
                {selectedLead?.bedrooms && <span className="bg-white/10 px-3 py-1 rounded-lg">{selectedLead.bedrooms}</span>}
                {selectedLead?.preferred_areas?.length > 0 && <span className="bg-white/10 px-3 py-1 rounded-lg">{selectedLead.preferred_areas.join(", ")}</span>}
              </div>
            </div>
          </div>

          {/* Deals grid */}
          <div className="bg-white rounded-xl border border-slate-200" data-testid="matched-deals-list">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">{matchedDeals.length} deals match · {picksCount} selected</p>
              {picksCount > 0 && (
                <button onClick={createSession} className="btn-primary text-sm flex items-center" data-testid="create-session-btn">
                  <Check className="w-4 h-4 mr-1" /> Save Top {picksCount} Picks
                </button>
              )}
            </div>
            {loading ? (
              <div className="p-12 text-center"><span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2342] inline-block" /></div>
            ) : matchedDeals.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No matching deals found</p>
                <p className="text-xs text-slate-400 mt-1">Try adding deals to your inventory that match this lead's criteria</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {matchedDeals.map(deal => {
                  const isSelected = !!selectedDeals[deal.id];
                  const tagInfo = TAG_LABELS[deal.marketing_tag];
                  return (
                    <div key={deal.id} className={`p-4 px-5 transition-colors ${isSelected ? 'bg-green-50 border-l-4 border-green-500' : 'hover:bg-slate-50'}`}
                      data-testid={`match-deal-${deal.id}`}>
                      <div className="flex items-center gap-4">
                        <button onClick={() => toggleDeal(deal)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${isSelected ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                          data-testid={`toggle-deal-${deal.id}`}>
                          {isSelected ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-[#0A2342]">{deal.property_name}</h4>
                            {deal.overall_score && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${deal.overall_score >= 90 ? 'bg-green-100 text-green-700' : deal.overall_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{deal.overall_score}</span>}
                            {tagInfo && <span className={`${tagInfo.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>{tagInfo.label}</span>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{deal.area} · {deal.bedrooms}{deal.special_offer ? ` · ${deal.special_offer}` : ""}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-[#DE6E4B]">${deal.total_monthly_cost?.toLocaleString()}<span className="text-xs font-normal text-slate-400">/mo</span></p>
                        </div>
                      </div>
                      {/* Editable fields when selected */}
                      {isSelected && (
                        <div className="mt-3 ml-12 flex flex-wrap gap-2 items-center">
                          <select value={selectedDeals[deal.id]?.marketing_tag || ""} onChange={e => updatePickDetail(deal.id, "marketing_tag", e.target.value)}
                            className="text-xs border rounded-lg px-2 py-1" data-testid={`pick-tag-${deal.id}`}>
                            <option value="">No tag</option>
                            {Object.entries(TAG_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          <input type="text" placeholder="Why this is a good pick..." value={selectedDeals[deal.id]?.marketing_note || ""}
                            onChange={e => updatePickDetail(deal.id, "marketing_note", e.target.value)}
                            className="flex-1 text-xs border rounded-lg px-3 py-1 min-w-[200px]" data-testid={`pick-note-${deal.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Review (shows saved sessions) + History */}
      {(step === "review" || showHistory) && (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="sessions-list">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-[#0A2342]">Saved Top Picks Sessions</h2>
            <AiModelSelect value={aiModel} onChange={setAiModel} className="shrink-0" />
          </div>
          {sessions.length === 0 ? (
            <div className="p-12 text-center text-slate-500">No sessions yet</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {sessions.map(session => (
                <SessionCard key={session.id} session={session} sending={sending} onSend={sendTopPicks} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Plus = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;

const SessionCard = ({ session, sending, onSend }) => {
  const [expanded, setExpanded] = useState(false);
  const isSent = session.status === "sent";

  return (
    <div className="p-5" data-testid={`session-${session.id}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-[#0A2342]">{session.lead_name}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isSent ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {isSent ? "Sent" : "Draft"}
            </span>
            {session.ai_generated && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">AI</span>
            )}
            <span className="text-xs text-slate-400">{session.deals?.length} picks</span>
          </div>
          <p className="text-sm text-slate-500">{session.lead_email} · {new Date(session.created_at).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isSent && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onSend(session.id, "email"); }}
                disabled={sending === session.id + "email"}
                className="px-3 py-1.5 bg-[#0A2342] text-white text-xs rounded-lg hover:bg-[#0d2d54] flex items-center gap-1"
                data-testid={`send-email-${session.id}`}>
                {sending === session.id + "email" ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Mail className="w-3 h-3" />} Email
              </button>
              <button onClick={(e) => { e.stopPropagation(); onSend(session.id, "sms"); }}
                disabled={sending === session.id + "sms"}
                className="px-3 py-1.5 bg-[#8A9A5B] text-white text-xs rounded-lg hover:bg-[#7a8a4b] flex items-center gap-1"
                data-testid={`send-sms-${session.id}`}>
                {sending === session.id + "sms" ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <MessageCircle className="w-3 h-3" />} SMS
              </button>
              <button onClick={(e) => { e.stopPropagation(); onSend(session.id, "both"); }}
                disabled={sending === session.id + "both"}
                className="px-3 py-1.5 bg-[#DE6E4B] text-white text-xs rounded-lg hover:bg-[#c5593d] flex items-center gap-1"
                data-testid={`send-both-${session.id}`}>
                {sending === session.id + "both" ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Send className="w-3 h-3" />} Both
              </button>
            </>
          )}
          {isSent && <p className="text-xs text-green-600">Sent via {session.sent_via} on {new Date(session.sent_at).toLocaleDateString()}</p>}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="mt-4 space-y-2">
              {session.deals?.map((d, i) => {
                const tagInfo = TAG_LABELS[d.marketing_tag];
                return (
                  <div key={d.deal_id || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <span className="text-lg font-bold text-[#0A2342] w-6 text-center">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-[#0A2342]">{d.property_name}</p>
                        {tagInfo && <span className={`${tagInfo.color} text-white text-[9px] font-bold px-2 py-0.5 rounded-full`}>{tagInfo.label}</span>}
                      </div>
                      <p className="text-xs text-slate-500">{d.area} · {d.bedrooms}</p>
                      {d.marketing_note && <p className="text-xs text-slate-500 italic mt-0.5">"{d.marketing_note}"</p>}
                    </div>
                    <p className="text-base font-bold text-[#DE6E4B]">${d.total_monthly_cost?.toLocaleString()}/mo</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopPicks;
